import { useState, useEffect } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/layout/Layout';
import Header from '../components/layout/Header';
import KPICard from '../components/dashboard/KPICard';
import TrendChart from '../components/dashboard/TrendChart';
import DataTable from '../components/dashboard/DataTable';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Building2, 
  Shield, 
  Megaphone, 
  DollarSign, 
  Users,
  TrendingUp,
  TrendingDown,
  AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExecutiveScorecard = () => {
  const { selectedYear, periodType, selectedPeriod } = useApp();
  const { isAdmin, isELT } = useAuth();
  const [loading, setLoading] = useState(true);
  const [scorecard, setScorecard] = useState(null);
  
  // Delete KPI state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [kpiToDelete, setKpiToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Comment counts per function
  const [commentCounts, setCommentCounts] = useState({});

  useEffect(() => {
    if (selectedPeriod) {
      fetchScorecard();
      fetchAllCommentCounts();
    }
  }, [selectedYear, periodType, selectedPeriod]);

  const fetchScorecard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/dashboard/executive/scorecard`, {
        params: { 
          year: selectedYear, 
          period_type: periodType,
          period_start: selectedPeriod?.startDate,
          period_end: selectedPeriod?.endDate
        }
      });
      setScorecard(response.data);
    } catch (error) {
      console.error('Failed to fetch scorecard:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch comment counts for all functions
  const fetchAllCommentCounts = async () => {
    const functions = ['brokerage', 'mga', 'marketing', 'finance', 'people'];
    const counts = {};
    
    try {
      await Promise.all(functions.map(async (func) => {
        try {
          const response = await axios.get(`${API}/comments/count`, {
            params: {
              function: func,
              year: selectedYear,
              period_type: periodType,
              period_start_date: selectedPeriod?.startDate
            }
          });
          counts[func] = response.data.counts || {};
        } catch (error) {
          counts[func] = {};
        }
      }));
      setCommentCounts(counts);
    } catch (error) {
      console.error('Failed to fetch comment counts:', error);
    }
  };

  // Handle KPI deletion
  const handleDeleteKpi = (kpiId, kpiName) => {
    setKpiToDelete({ id: kpiId, name: kpiName });
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteKpi = async () => {
    if (!kpiToDelete) return;
    
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/kpi-definitions/${kpiToDelete.id}`);
      toast.success(`"${kpiToDelete.name}" removed from dashboard`);
      setIsDeleteDialogOpen(false);
      setKpiToDelete(null);
      fetchScorecard(); // Refresh scorecard
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove KPI');
    } finally {
      setIsDeleting(false);
    }
  };

  const functionIcons = {
    brokerage: Building2,
    mga: Shield,
    marketing: Megaphone,
    finance: DollarSign,
    people: Users
  };

  const functionLabels = {
    brokerage: 'Brokerage',
    mga: 'MGA',
    marketing: 'Marketing',
    finance: 'Finance',
    people: 'People'
  };

  const getRAGStatus = (metric) => {
    // Use status from backend if available
    if (metric.rag_status) return metric.rag_status;
    
    // Fallback calculation
    if (!metric.has_data) return 'no_data';
    if (!metric.plan_value) return 'no_plan';
    const variance = (metric.actual_value - metric.plan_value) / Math.abs(metric.plan_value);
    const isLowerBetter = metric.directionality === 'lower_is_better';
    const adjustedVariance = isLowerBetter ? -variance : variance;
    
    if (adjustedVariance >= 0) return 'green';
    if (adjustedVariance >= -0.05) return 'yellow';
    return 'red';
  };

  const getSummaryStats = () => {
    if (!scorecard?.scorecard) return { green: 0, yellow: 0, red: 0, noData: 0, total: 0, withData: 0 };
    
    let green = 0, yellow = 0, red = 0, noData = 0, total = 0, withData = 0;
    
    Object.values(scorecard.scorecard).forEach(func => {
      // Use summary from backend if available
      if (func.summary) {
        green += func.summary.green || 0;
        yellow += func.summary.yellow || 0;
        red += func.summary.red || 0;
        noData += func.summary.no_data || 0;
        total += func.summary.total_definitions || func.metrics.length;
        withData += func.summary.with_data || 0;
      } else {
        // Fallback calculation
        func.metrics.forEach(metric => {
          const status = getRAGStatus(metric);
          if (status === 'green') green++;
          else if (status === 'yellow') yellow++;
          else if (status === 'red') red++;
          else if (status === 'no_data') noData++;
          total++;
          if (metric.has_data) withData++;
        });
      }
    });
    
    return { green, yellow, red, noData, total, withData };
  };

  const stats = getSummaryStats();

  if (loading) {
    return (
      <Layout>
        <Header title="Executive Scorecard" subtitle="Consolidated KPI roll-up" />
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-20 sm:h-24 rounded-lg" />
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-40 sm:h-48 rounded-lg" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header title="Executive Scorecard" subtitle={selectedPeriod?.label || 'Consolidated KPI roll-up'} />
      
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6" data-testid="executive-scorecard">
        {/* Summary Cards - 2 cols on mobile, 5 on desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
          <div className="bg-white rounded-lg border border-[#E5E7EB] p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm font-medium text-[#6B7280]">Total KPIs</span>
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-[#9CA3AF]" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-[#111827] mt-1 sm:mt-2">{stats.total}</p>
            <p className="text-xs text-[#9CA3AF] mt-1">{stats.withData} with data</p>
          </div>
          
          <div className="bg-[#D1FAE5] rounded-lg border border-[#34D399] p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm font-medium text-[#065F46]">On Track</span>
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-[#059669]" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-[#065F46] mt-1 sm:mt-2">{stats.green}</p>
          </div>
          
          <div className="bg-[#FEF3C7] rounded-lg border border-[#FCD34D] p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm font-medium text-[#92400E]">At Risk</span>
              <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-[#D97706]" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-[#92400E] mt-1 sm:mt-2">{stats.yellow}</p>
          </div>
          
          <div className="bg-[#FEE2E2] rounded-lg border border-[#FCA5A5] p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm font-medium text-[#991B1B]">Off Track</span>
              <TrendingDown className="w-4 h-4 sm:w-5 sm:h-5 text-[#DC2626]" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-[#991B1B] mt-1 sm:mt-2">{stats.red}</p>
          </div>
          
          <div className="bg-[#F1F5F9] rounded-lg border border-[#CBD5E1] p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm font-medium text-[#64748B]">No Data</span>
              <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-[#94A3B8]" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-[#64748B] mt-1 sm:mt-2">{stats.noData}</p>
          </div>
        </div>

        {/* Function Sections */}
        {scorecard?.scorecard && Object.entries(scorecard.scorecard).map(([func, data]) => {
          const Icon = functionIcons[func] || Building2;
          const summary = data.summary || {};
          
          return (
            <div key={func} className="bg-white rounded-lg border border-[#E5E7EB] p-4 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-[#F3F4F6] flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#6B7280]" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-base sm:text-lg font-semibold text-[#111827] truncate">
                    {functionLabels[func]}
                  </h2>
                  <p className="text-xs sm:text-sm text-[#6B7280]">
                    {summary.total_definitions || data.metrics.length} KPIs defined
                    {summary.no_data > 0 && (
                      <span className="text-[#94A3B8]"> • {summary.no_data} awaiting data</span>
                    )}
                  </p>
                </div>
                {/* Mini status summary */}
                <div className="hidden sm:flex items-center gap-2 text-xs">
                  {summary.green > 0 && <span className="text-[#059669]">●{summary.green}</span>}
                  {summary.yellow > 0 && <span className="text-[#D97706]">●{summary.yellow}</span>}
                  {summary.red > 0 && <span className="text-[#DC2626]">●{summary.red}</span>}
                  {summary.no_data > 0 && <span className="text-[#94A3B8]">●{summary.no_data}</span>}
                </div>
              </div>
              
              {/* KPI Cards - Show all KPIs, responsive grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3 sm:gap-4 items-start">
                {data.metrics.map((metric) => (
                  <KPICard
                    key={metric.metric_name}
                    title={metric.metric_name}
                    value={metric.has_data ? metric.actual_value : null}
                    unit={metric.unit}
                    planValue={metric.has_data ? metric.plan_value : null}
                    variance={metric.has_data && metric.variance ? metric.variance : null}
                    ragStatus={metric.rag_status || getRAGStatus(metric)}
                    kpiId={metric.definition_id}
                    onDelete={handleDeleteKpi}
                    canEdit={isELT()}
                    // Period context for inline editing
                    periodType={periodType}
                    periodStartDate={selectedPeriod?.startDate}
                    periodEndDate={selectedPeriod?.endDate}
                    year={selectedYear}
                    functionName={func}
                    onDataUpdate={fetchScorecard}
                    // Last updated info
                    updatedBy={metric.updated_by_name || metric.uploaded_by_name}
                    updatedAt={metric.updated_at}
                    // Comment count
                    commentCount={commentCounts[func]?.[metric.metric_name] || 0}
                  />
                ))}
              </div>
            </div>
          );
        })}

        {/* Empty State */}
        {(!scorecard?.scorecard || Object.keys(scorecard.scorecard).length === 0) && (
          <div className="bg-white rounded-lg border border-[#E5E7EB] p-8 sm:p-12 text-center">
            <TrendingUp className="w-10 h-10 sm:w-12 sm:h-12 text-[#D1D5DB] mx-auto mb-3 sm:mb-4" />
            <h3 className="text-base sm:text-lg font-medium text-[#111827] mb-2">No KPI data available</h3>
            <p className="text-sm text-[#6B7280]">
              Upload Excel data in the Upload Center to see your KPI metrics here.
            </p>
          </div>
        )}

        {/* Last Updated */}
        {scorecard?.last_updated && (
          <p className="text-xs sm:text-sm text-[#9CA3AF] text-right">
            Last updated: {new Date(scorecard.last_updated).toLocaleString()}
          </p>
        )}
      </div>

      {/* Delete KPI Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-rose-600">Remove KPI from Dashboard</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove &quot;{kpiToDelete?.name}&quot; from the dashboard? 
              The KPI data will be preserved and can be restored later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDeleteKpi}
              disabled={isDeleting}
              data-testid="confirm-delete-kpi-btn"
            >
              {isDeleting ? 'Removing...' : 'Remove KPI'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default ExecutiveScorecard;
